/**
 * Key Stats 2x2 grid inside the Rehab detail drawer. Mirrors the
 * `.dr-stat-grid` block in rehab.html: Days · Budget Used · Remaining ·
 * Daily Burn.
 */
import { cn } from "@/lib/utils";
import type { RehabProject } from "@/hooks/use-rehab";

import type { ScopeItem } from "./rehab-scope";
import { daysBetween, deriveBudget, deriveBudgetFromItems } from "./rehab-status";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface RehabStatsGridProps {
  project: RehabProject;
  /**
   * Live scope items from the drawer's `rehab_line_items` query. When
   * provided, budget figures are derived from these (so custom items and
   * in-flight actual-cost edits are reflected immediately) instead of the
   * project's cached `line_*_total` totals.
   */
  items?: ScopeItem[];
}

export function RehabStatsGrid({ project, items }: RehabStatsGridProps) {
  const days = daysBetween(project.start_date, project.actual_completion);
  const budget = items ? deriveBudgetFromItems(items) : deriveBudget(project);
  const remaining = budget.budget - budget.used;
  // Burn = spend spread across the elapsed days, counting the first day as
  // day 1 (Math.max(1, days)) so a rehab that starts and finishes the same
  // day (days = 0) still reports its spend instead of dividing by zero. The
  // "no spend yet" state is driven by actual spend, not by the day count.
  const hasSpend = budget.used > 0;
  const burnPerDay = hasSpend ? budget.used / Math.max(1, days) : 0;

  const stats: Array<{
    label: string;
    value: string;
    sub: string;
    tone?: "neg" | "pos";
  }> = [
    {
      label: "Days in Rehab",
      value: String(days),
      sub: `Started ${formatDate(project.start_date)}${
        project.actual_completion ? ` · Done ${formatDate(project.actual_completion)}` : ""
      }`,
    },
    {
      label: "Budget Used",
      value: formatCurrency(budget.used),
      sub: `${budget.pct}% of ${formatCurrency(budget.budget)}`,
      tone: budget.overBudget ? "neg" : undefined,
    },
    {
      label: "Remaining",
      value: formatCurrency(Math.abs(remaining)),
      sub: remaining < 0 ? "Over budget" : "Available to spend",
      tone: remaining < 0 ? "neg" : "pos",
    },
    {
      label: "Daily Burn",
      value: hasSpend ? formatCurrency(burnPerDay) : "—",
      sub: hasSpend ? "Avg per day" : "No spend yet",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="flex flex-col gap-1 rounded-lg border border-border bg-card p-3"
        >
          <span className="text-xs font-semibold text-muted-foreground">
            {stat.label}
          </span>
          <span
            className={cn(
              "text-xl font-extrabold leading-tight tabular-nums",
              stat.tone === "neg" && "text-destructive",
              stat.tone === "pos" && "text-success",
              !stat.tone && "text-foreground",
            )}
          >
            {stat.value}
          </span>
          <span className="text-[11.5px] text-muted-foreground">{stat.sub}</span>
        </div>
      ))}
    </div>
  );
}
